/**
 * useAgents Hook
 * AIDLC Administrative Backend
 *
 * Provides agent data fetching with both polling AND WebSocket real-time updates.
 * The Agent Dashboard is the landing page -- this hook powers it.
 *
 * When WebSocket is connected and bridge is active:
 *   - Agent status changes are pushed via 'agent:status' events
 *   - The hook patches the local agent list immediately (no re-fetch needed)
 *   - Polling remains active as a fallback (30s interval)
 *
 * When WebSocket is disconnected:
 *   - Falls back to polling only (30s interval)
 *
 * Tracks lastBridgeActivity timestamp for the "last updated X seconds ago" indicator.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Agent } from '../types/agent';
import { getAllAgents, getAgentById } from '../services/agentService';

const DEFAULT_POLL_INTERVAL = 30000; // 30 seconds

interface UseAgentsResult {
  agents: Agent[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  lastBridgeUpdate: string | null;
  bridgeActive: boolean;
}

interface UseAgentResult {
  agent: Agent | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface AgentStatusEvent {
  agentId: string;
  status: string;
  task: string | null;
  timestamp: string;
  tokensUsed: number;
  sessionId: string;
  previousStatus: string;
  agent: Agent;
}

/**
 * Fetch all agents with automatic polling AND WebSocket real-time updates.
 * Pass a Socket.io socket instance to enable real-time bridge updates.
 */
export function useAgents(
  pollInterval = DEFAULT_POLL_INTERVAL,
  socket?: { on: (event: string, handler: (...args: unknown[]) => void) => void; off: (event: string, handler: (...args: unknown[]) => void) => void; connected?: boolean } | null,
): UseAgentsResult {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastBridgeUpdate, setLastBridgeUpdate] = useState<string | null>(null);
  const [bridgeActive, setBridgeActive] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAgents = useCallback(async () => {
    try {
      const data = await getAllAgents();
      setAgents(data);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch agents';
      setError(message);
      console.error('[useAgents] Fetch failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Polling fallback
  useEffect(() => {
    fetchAgents();

    if (pollInterval > 0) {
      intervalRef.current = setInterval(fetchAgents, pollInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchAgents, pollInterval]);

  // WebSocket real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleAgentStatus = (...args: unknown[]) => {
      const data = args[0] as AgentStatusEvent;
      if (!data || !data.agentId) return;

      setAgents((prev) => {
        const idx = prev.findIndex((a) => a.id === data.agentId);
        if (idx === -1) return prev;

        const updated = [...prev];
        // If the server sent the full agent object, use it; otherwise patch
        if (data.agent) {
          updated[idx] = data.agent;
        } else {
          updated[idx] = {
            ...updated[idx],
            status: data.status as Agent['status'],
            statusTask: data.task,
            statusTimestamp: data.timestamp,
          };
          if (data.tokensUsed > 0) {
            updated[idx] = {
              ...updated[idx],
              statistics: {
                ...updated[idx].statistics,
                totalTokensUsed: updated[idx].statistics.totalTokensUsed + data.tokensUsed,
                lastActiveDate: data.timestamp,
              },
            };
          }
        }
        return updated;
      });

      setLastBridgeUpdate(data.timestamp);
    };

    const handleBridgeReady = (...args: unknown[]) => {
      const data = args[0] as { active: boolean };
      if (data && data.active) {
        setBridgeActive(true);
      }
    };

    const handleBridgeStatus = (...args: unknown[]) => {
      const data = args[0] as { active: boolean };
      if (data) {
        setBridgeActive(!!data.active);
      }
    };

    const handleBridgeActivity = (...args: unknown[]) => {
      const data = args[0] as { timestamp: string };
      if (data && data.timestamp) {
        setLastBridgeUpdate(data.timestamp);
      }
    };

    socket.on('agent:status', handleAgentStatus);
    socket.on('bridge:ready', handleBridgeReady);
    socket.on('bridge:status', handleBridgeStatus);
    socket.on('bridge:activity', handleBridgeActivity);

    return () => {
      socket.off('agent:status', handleAgentStatus);
      socket.off('bridge:ready', handleBridgeReady);
      socket.off('bridge:status', handleBridgeStatus);
      socket.off('bridge:activity', handleBridgeActivity);
    };
  }, [socket]);

  return { agents, isLoading, error, refresh: fetchAgents, lastBridgeUpdate, bridgeActive };
}

/**
 * Fetch a single agent by ID
 */
export function useAgent(agentId: string | null): UseAgentResult {
  const [agent, setAgent] = useState<Agent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAgent = useCallback(async () => {
    if (!agentId) return;
    setIsLoading(true);
    try {
      const data = await getAgentById(agentId);
      setAgent(data);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch agent';
      setError(message);
      console.error('[useAgent] Fetch failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  return { agent, isLoading, error, refresh: fetchAgent };
}
